import './style.scss'
import { Button, Checkbox, message, Modal, Pagination, Tooltip } from 'antd'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Api from '@/api/home'
import Empty from '@/assets/empty.png'
import { UserStore } from '@/global-states'
import { eventTracking, getLanguage } from '@/libs/util'
import {
  BlockOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  NodeCollapseOutlined
} from '@ant-design/icons'
import VideoBox from './components/VideoBox'

const Index: FC = () => {
  const { t: translate } = useTranslation(['video', 'system'])
  const { creationList } = UserStore
  const navigate = useNavigate()
  const editRef = useRef<any>()
  const [data, setData] = useState<any[]>(undefined as any)
  const [mode, setMode] = useState<any>()
  const page = useRef<any>()
  const size = useRef<any>()
  const total = useRef<any>()
  const [opOpen, setOpOpen] = useState(false)
  const [checkDatas, setCheckDatas] = useState<any[]>([])
  const [percent, setPercent] = useState(-1)

  const opData = useMemo(() => {
    return data?.filter((d) => d.status !== 1)
  }, [data])
  const modeList = [
    {
      title: translate('video.all')
    },
    {
      id: 0,
      title: translate('video.points_mode')
    },
    {
      id: 1,
      title: translate('video.dark_mode')
    }
  ]

  useEffect(() => {
    size.current = window.innerWidth < 1800 ? 20 : 30
    eventTracking('MyCreationsPage')
  }, [])

  useEffect(() => {
    page.current = 0
    getData()
  }, [mode])

  useEffect(() => {
    if (data?.length) {
      setData(
        data?.map((d) => {
          const f = creationList.find((c: any) => c.id === d.id)
          if (f) {
            return f
          }
          return d
        })
      )
    }
  }, [creationList])

  const getData = async () => {
    if (editRef.current) return
    const device_id = UserStore.device_id
    const res = await Api.getCreations({
      page: page.current || 0,
      size: size.current,
      di: device_id,
      mode
    })
    total.current = res?.total || 0
    setData(res?.list || [])
  }

  const onPaginationChange = (p: number, s: number) => {
    page.current = p - 1
    size.current = s
    setCheckDatas([])
    getData()
  }

  const onCheckChange = (d: any, check: boolean) => {
    if (check) {
      setCheckDatas([...checkDatas, d])
    } else {
      setCheckDatas(checkDatas.filter((c) => c.id !== d.id))
    }
  }

  const batchDownload = async () => {
    const zip = new JSZip()
    const fileNames: string[] = []
    const postDatas = checkDatas.filter((d) => d.status === 2)
    let completedDownloadNum = 0

    setPercent(0)

    const fetchPromises = postDatas.map(async (data) => {
      try {
        const { title, url } = data
        const response = await fetch(url)
        const blob = await response.blob()
        completedDownloadNum = completedDownloadNum + 1
        setPercent(Math.min(Math.floor((completedDownloadNum * 100) / postDatas.length), 99))
        const u = url.split('?')[0]
        const extension = u.substring(u.lastIndexOf('.')).toLowerCase()
        let fileName = `${title}${extension}`
        const l = fileNames.filter((f) => f === fileName)
        fileNames.push(fileName)
        if (l?.length) {
          fileName = `${title} (${l.length})${extension}`
        }

        zip.file(fileName, blob, { binary: true })
      } catch (error) {
        setPercent(-1)
        console.error(translate('video.download_failed'))
      }
    })

    await Promise.all(fetchPromises)

    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, translate('video.download_result'))
    setPercent(100)
    message.success(translate('video.download_success'))
    setTimeout(() => {
      setPercent(-1)
      setCheckDatas([])
      setOpOpen(false)
    }, 500)
  }

  const batchDelete = async () => {
    Modal.confirm({
      title: translate('video.batch_delete_title'),
      content: translate('video.batch_delete_content'),
      okButtonProps: {
        danger: true
      },
      icon: <InfoCircleOutlined style={{ color: '#e54945' }} />,
      onOk: async () => {
        const id_list = checkDatas.map((d) => d.id)
        await Api.batchDeleteCreations({
          id_list
        })
        message.success(translate('video.batch_delete_success'))
        setCheckDatas([])
        setOpOpen(false)
        getData()
      }
    })
  }

  return (
    <div className="page-common page-video">
      <div className="common-header">{translate('video.management_title')}</div>
      <div className="common-wrapper">
        {data && (
          <div className="page-video-main">
            <div className="page-video-wrapper">
              <div className="nav">
                {modeList.map((s) => (
                  <div
                    className={`nav-item ${s.id === mode ? 'actived' : ''}`}
                    key={s.id}
                    onClick={() => setMode(s.id)}
                  >
                    <label className={s.id === mode ? 'gradient' : ''}>{s.title}</label>
                  </div>
                ))}

                <div className="op" onClick={() => setOpOpen(!opOpen)}>
                  {opOpen ? <NodeCollapseOutlined /> : <BlockOutlined />}
                  {opOpen ? (
                    <span className="gradient">{translate('video.exit_batch_operation')}</span>
                  ) : (
                    translate('video.batch_operation')
                  )}
                </div>
              </div>

              {!!data.length && (
                <div className="page-container">
                  {data?.map((d) => (
                    <VideoBox
                      key={d.id}
                      d={d}
                      update={getData}
                      opOpen={opOpen}
                      checked={checkDatas.some((s) => s.id === d.id) ? true : false}
                      onCheckChange={onCheckChange.bind(this, d)}
                    />
                  ))}
                </div>
              )}

              {!data.length && (
                <div className="data-empty">
                  <img className="empty" src={Empty} />
                  <p>{translate('video.no_creations')}</p>
                  <Button type="primary" onClick={() => navigate(`/create-video`)}>
                    {translate('video.go_create')}
                  </Button>
                </div>
              )}
            </div>
            {total.current > 0 && !!data.length && !opOpen && (
              <Pagination
                showSizeChanger
                current={(page.current || 0) + 1}
                total={total.current}
                pageSize={size.current || 20}
                showTotal={(total) => translate('video.total_items', { total })}
                onChange={onPaginationChange}
                align="end"
              />
            )}
          </div>
        )}
      </div>
      {opOpen && (
        <div className={`batch-operation ${getLanguage()}`}>
          <div
            className="left"
            onClick={() => {
              if (opData?.length === checkDatas?.length) {
                setCheckDatas([])
              } else {
                setCheckDatas(opData)
              }
            }}
          >
            <Checkbox
              indeterminate={!!checkDatas?.length && checkDatas.length < opData?.length}
              checked={opData?.length === checkDatas?.length}
            ></Checkbox>
            <span className="op">
              {opData?.length === checkDatas?.length ? translate('system.cancel') : translate('system.select_all')}
            </span>
            <span>
              （{translate('video.selected_count')}
              <label className="gradient num">
                {translate('video.selected_count_num', {
                  count: checkDatas?.length
                })}
              </label>
              ）
            </span>
          </div>

          <div className="right">
            <Tooltip placement="top" title={checkDatas.length ? '' : translate('video.select_items_tooltip')}>
              <Button
                loading={percent > -1}
                disabled={!checkDatas.length}
                icon={<DownloadOutlined />}
                type="primary"
                onClick={batchDownload}
              >
                {translate('system.download')}
                {percent > -1 && ` ${percent}%`}
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={checkDatas.length ? '' : translate('video.select_items_tooltip')}>
              <Button
                disabled={!checkDatas.length || percent > -1}
                icon={<DeleteOutlined />}
                type="primary"
                onClick={batchDelete}
              >
                {translate('system.delete')}
              </Button>
            </Tooltip>

            <CloseOutlined onClick={() => setOpOpen(false)} />
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(Index)
